import { FIELD } from "./fieldKinds.js";
import { formatDateRange, formatLocation } from "../lib/format.js";
import { RSVP_REGISTRATION_FIELDS } from "../features/registration/formConfig.js";
import {
  compose, coverSection, aboutSection, gallerySection,
  agendaSection, rsvpSection, subscribeSection, closingSection,
} from "../features/brochure/sections.js";

// Wedding event type — couple, story, events/itinerary, gallery, RSVP.
export default {
  id: "wedding",
  category: "personal",
  label: "Wedding",
  blurb: "Save the date — story, events, gallery, RSVP.",
  icon: "💍",
  wizardSteps: [
    {
      id: "couple",
      title: "The couple",
      fields: [
        { key: "title", label: "Couple names", kind: FIELD.TEXT, required: true, placeholder: "Aarav & Diya" },
        { key: "subtitle", label: "Tagline", kind: FIELD.TEXT, placeholder: "We're getting married!" },
        { key: "heroImage", label: "Hero photo", kind: FIELD.HERO_IMAGE, previewPage: "cover" },
      ],
    },
    {
      id: "when-where",
      title: "When & where",
      fields: [
        { key: "startDate", label: "Wedding date", kind: FIELD.DATE, required: true },
        { key: "endDate", label: "End date (if multi-day)", kind: FIELD.DATE },
        { key: "venue", label: "Venue", kind: FIELD.TEXT },
        { key: "city", label: "City", kind: FIELD.TEXT },
        { key: "country", label: "Country", kind: FIELD.TEXT },
        { key: "mapsUrl", label: "Google Maps link", kind: FIELD.URL, placeholder: "https://maps.google.com/...", previewPage: "cover" },
      ],
    },
    {
      id: "story",
      title: "Story, hosts & RSVP",
      fields: [
        { key: "description", label: "Your story / invitation message", kind: FIELD.TEXTAREA, previewPage: "details" },
        { key: "registrationUrl", label: "External RSVP link (optional)", kind: FIELD.URL, previewPage: "cover" },
        {
          key: "collectEventRegistrations",
          label: "Collect RSVPs inside einvite",
          kind: FIELD.TOGGLE,
          previewPage: "eventRegistration",
          description: "Adds an RSVP form so guests can respond. Entries stay visible only to you.",
          defaultPatch: { eventRegistrationFields: RSVP_REGISTRATION_FIELDS.map((field) => ({ ...field })) },
        },
        {
          key: "eventRegistrationFields",
          label: "RSVP form builder",
          kind: FIELD.REGISTRATION_FIELDS,
          previewPage: "eventRegistration",
          showWhen: (details) => !!details.collectEventRegistrations,
        },
        { key: "contacts", label: "Hosts / family contacts", kind: FIELD.CONTACTS, previewPage: "closing" },
        {
          key: "collectPreRegistration",
          label: "Subscribe for updates",
          kind: FIELD.TOGGLE,
          previewPage: "subscribe",
          description: "A simple public form for guests to get updates by mobile or email.",
        },
      ],
    },
    {
      id: "events",
      title: "Events",
      fields: [
        { key: "schedule", label: "Wedding events (Mehndi, Sangeet, Ceremony, Reception…)", kind: FIELD.SCHEDULE, itemLabel: "function", previewPage: "schedule" },
      ],
    },
    {
      id: "gallery",
      title: "Gallery",
      fields: [
        { key: "customSlides", label: "Photo gallery", kind: FIELD.CUSTOM_SLIDES, previewPage: "customSlide" },
      ],
    },
  ],
  planPages(event) {
    const details = event.details || {};
    return compose(
      coverSection(event, { ctaLabel: "RSVP →" }),
      aboutSection(details, { heading: "Our Story" }),
      agendaSection(details, "schedule", { title: "Events" }),
      gallerySection(details),
      rsvpSection(event),
      subscribeSection(event),
      closingSection(details, { heading: "With love", ctaLabel: "RSVP →" }),
    );
  },
  deriveOg(details = {}) {
    const when = formatDateRange(details.startDate, details.endDate);
    const where = formatLocation(details);
    return {
      title: details.title || "We're getting married",
      description: [details.subtitle, when, where].filter(Boolean).join(" · "),
    };
  },
};
